import VueCookies from 'vue-cookies'
import existingCourseIdsWithImages from './exisitingCourseIdsWithImages'

const getDomain = () => {
  switch (process.env.VUE_APP_ENVIRONMENT) {
    case 'production':
      return '.ine.com'
    case 'staging':
      return '.staging.ine.com'
    case 'development':
      return '.development.ine.com'
    case 'local':
      return 'localhost'
    default:
      return '.ine.com'
  }
}

const getToken = () => {
  return new Promise((resolve, reject) => {
    try {
      const accessToken = window.localStorage.getItem('authToken')
      resolve(accessToken)
    } catch (error) {
      reject(error)
    }
  })
}

const setToken = token => {
  return new Promise((resolve, reject) => {
    try {
      window.localStorage.setItem('authToken', token)
      VueCookies.set('ine_access_token', token, '1y', '/', getDomain(), false)
      resolve(token)
    } catch (error) {
      reject(error)
    }
  })
}

const clearToken = () => {
  window.localStorage.removeItem('authToken')
  VueCookies.set('ine_access_token', '', '1hr', '/', getDomain())
  VueCookies.remove('ine_access_token', '/', getDomain())
  VueCookies.remove('ine_session')
}

function lazyLoadComponent({ componentFactory, loading, loadingData }) {
  let resolveComponent

  return () => ({
    component: new Promise(resolve => {
      resolveComponent = resolve
    }),
    loading: {
      mounted() {
        if (!('IntersectionObserver' in window)) {
          componentFactory().then(resolveComponent)
          return
        }

        const observer = new IntersectionObserver(entries => {
          if (entries[0].intersectionRatio <= 0) return
          observer.unobserve(this.$el)
          componentFactory().then(resolveComponent)
        })
        observer.observe(this.$el)
      },
      render(createElement) {
        return createElement(loading, loadingData)
      },
    },
  })
}

const duration = {
  lowerBound(num, limit) {
    return num >= limit ? num : limit
  },
  durationOn(totalDuration, type) {
    const colon = totalDuration.split(':')
    let seconds = +colon[0] * 60 * 60 + +colon[1] * 60 + +colon[2]
    seconds = Number(seconds)
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor((seconds % 3600) % 60)
    const hDisplay = h > 0 ? h + (h === 1 ? 'h ' : 'h ') : ''
    const mDisplay = m > 0 ? m + (m === 1 ? 'm ' : 'm ') : ''
    const sDisplay = s > 0 ? s + (s === 1 ? 's' : 's') : ''
    if (type === 'hours') {
      return hDisplay
    }
    if (type === 'minutes') {
      return hDisplay + mDisplay
    }
    return hDisplay + mDisplay + sDisplay
  },
  pad(num) {
    return `0${num}`.slice(-2)
  },
  convertToSeconds(time) {
    const split = time.split(':')
    return +split[0] * 60 * 60 + +split[1] * 60 + +split[2]
  },

  hhmmss(secs) {
    let minutes = Math.floor(secs / 60)
    secs %= 60
    const hours = Math.floor(minutes / 60)
    minutes %= 60
    return `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(secs)}`
  },

  durationFromSecs(sec) {
    return this.durationOn(this.hhmmss(sec))
  },
  getCourseDuration(course) {
    const rawDuration = course.duration.split(':')
    if (rawDuration.length === 3) {
      return `${rawDuration[0]}hrs ${rawDuration[1]}min`
    }
    return course.duration
  },
}

const getBackgroundImg = (course, size) => {
  if (existingCourseIdsWithImages.includes(course.id)) {
    const { small, medium, large } = course.backgrounds

    switch (size) {
      case 'small':
        return small
      case 'medium':
        return medium
      case 'large':
        return large
      default:
        return medium
    }
  } else {
    const host = 'https://courses.assets.ine.com'
    const rawImg = `${host}/courses/images/${course.id}/original.jpg`
    const smallImg = `${host}/fit-in/800x800/courses/images/${course.id}/original.jpg`
    const mediumImg = `${host}/fit-in/1200x1200/courses/images/${course.id}/original.jpg`

    switch (size) {
      case 'small':
        return smallImg
      case 'medium':
        return mediumImg
      case 'large':
        return rawImg
      default:
        return mediumImg
    }
  }
}

const getLearningPathBackground = id => {
  const images = [
    'LearningPath_background_CCNA_DataCenter.jpg',
    'LearningPath_background_CCNA_DataCenter_v2.jpg',
    'LearningPath_background_CCNA_RS.jpg',
    'LearningPath_background_CCNA_Security.jpg',
    'LearningPath_background_CCNA_Security_v3.jpg',
  ]
  const imgUrl = `https://assets-ine-com.s3.amazonaws.com/courses/learning+path/`
  switch (id) {
    case '00f8edb4-bd72-41ca-a423-6d31c8caebce':
      return `${imgUrl}${images[0]}`
    case '5f0dac37-559e-4670-bd38-546485345fe2':
      return `${imgUrl}${images[1]}`
    case '71d690ef-e707-49e0-aea9-a7d8f1a02c91':
      return `${imgUrl}${images[2]}`
    case '90b43bee-6cc7-43eb-b8d1-1d66f4aa0dcd':
      return `${imgUrl}${images[3]}`
    case 'a2ff47e9-018d-4f41-b8f2-9462e182abef':
      return `${imgUrl}${images[4]}`
    case 'e0130226-10f3-4391-bc5d-400381eb2efc':
      return `${imgUrl}${images[0]}`
    case 'c51c61f0-efeb-42b4-96e9-6a15f8e08b47':
      return `${imgUrl}${images[2]}`
    case 'bfae070e-4ed4-4036-990f-e32b1061f38f':
      return `${imgUrl}${images[4]}`
    case '57bb4e03-d841-4137-a1cc-9fb2b6ce362d':
      return `${imgUrl}${images[3]}`
    default:
      return `${imgUrl}${images[1]}`
  }
}

const getGradient = () => {
  // original: `to left bottom, rgb(239, 125, 34), rgb(249, 170, 13)`
  const gradients = [
    '#e04a13, #ef7d22',
    '#a82d40, #43349a',
    '#17861b, #9bdc1d',
    '#1f133a, #362153',
    '#48237b, #d52873',
    '#72237c, #d82775, #f5bf3b',
    '#081f63, #0f84a7, #40cbec',
    '#01a044, #0f3258',
    '#6c237d, #dc2d70, #e78a2d',
    '#ff2b4b, #43309c',
  ]
  const angle = 'to bottom right'
  return `${angle}, ${gradients[Math.floor(Math.random() * gradients.length)]}`
}

export {
  getDomain,
  getToken,
  setToken,
  clearToken,
  lazyLoadComponent,
  duration,
  getBackgroundImg,
  getLearningPathBackground,
  getGradient,
}
